import { useSearchParams } from 'react-router-dom'
import { useEffect } from 'react'
import toast from 'react-hot-toast'

// For OAuth redirects, we need the full backend URL (not relative path)
// VITE_API_URL should be the full URL in production (e.g., https://backend.example.com/api)
// For local dev, use VITE_BACKEND_URL or fallback to localhost:3001
const getBackendUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL

  // If VITE_API_URL is an absolute URL, use it
  if (apiUrl && (apiUrl.startsWith('http://') || apiUrl.startsWith('https://'))) {
    return apiUrl
  }

  // For local development, use explicit backend URL or default
  return import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001/api'
}

const BACKEND_URL = getBackendUrl()

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

export function Login() {
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const error = searchParams.get('error')
    if (error) {
      // Decode the error message
      const decodedError = decodeURIComponent(error)

      // Show user-friendly error messages
      if (decodedError.includes('@bluetagus.com')) {
        toast.error('Only @bluetagus.com accounts are allowed')
      } else if (decodedError === 'auth_failed') {
        toast.error('Authentication failed. Please try again.')
      } else {
        toast.error(decodedError)
      }
    }
  }, [searchParams])

  const handleGoogleSignIn = () => {
    window.location.href = `${BACKEND_URL}/auth/google`
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-50 pt-16 pb-24">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-white rounded-none border-b border-r border-secondary-200 shadow-none p-10 mx-auto max-w-sm sm:max-w-md">
          {/* Header */}
          <div className="text-center mb-8 flex flex-col items-center">
            <img 
              src="/bluetagus-logo.svg" 
              alt="Blue Tagus Logo" 
              className="w-48 h-auto mb-4"
            />
            <p className="text-secondary-600 font-sans mt-2">Sales Database Intelligence</p>
          </div>

          {/* Divider */}
          <div className="border-t border-secondary-100 mb-8"></div>

          {/* Sign In Section */}
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-primary-900 mb-2">Sign in</h2>
            <p className="text-secondary-600 mb-6 font-sans">Use your Google account to continue</p>

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-between px-4 py-3 border border-secondary-300 bg-transparent text-secondary-900 hover:bg-secondary-50 transition-colors duration-300 group"
            >
              <div className="flex items-center">
                <div className="w-8 h-8 bg-primary-50 flex items-center justify-center mr-3 border border-primary-200 group-hover:border-primary-300 transition-colors">
                  <span className="text-primary-800 font-heading font-semibold text-xs">BT</span>
                </div>
                <div className="text-left font-sans">
                  <div className="text-sm font-semibold">Sign in with Google</div>
                  <div className="text-xs text-secondary-500">@bluetagus.com</div>
                </div>
              </div>
              <GoogleIcon />
            </button>

            {/* Info Text */}
            <div className="mt-6 space-y-1">
              <p className="text-sm text-secondary-600">
                Authorized users only
              </p>
              <p className="text-xs text-secondary-400">
                Contact your administrator to request access
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4 text-xs text-secondary-400">
          <p>Secure access for authorized personnel only</p>
        </div>
      </div>
    </div>
  )
}
